/**
 * App Component
 * Root component with router setup
 */

import { useEffect, Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { AppProviders } from '@/providers';
import { Router } from '@/router/Router';
import { initializeDevTools } from '@/common/stores/devtools';
import { useAuthInit } from '@/common/auth';
import LoadingScreen from '@/common/components/LoadingScreen';

interface ErrorBoundaryState {
  hasError: boolean;
}

class AppErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppErrorBoundary]', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: '16px' }}>
          <p style={{ fontSize: '1rem', color: '#71717a' }}>Something went wrong. Please refresh the page.</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '8px 24px', borderRadius: '999px', background: '#0a0a0a', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.875rem' }}
          >
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppInner() {
  const initialized = useAuthInit();

  if (!initialized) {
    return <LoadingScreen message="Loading" />;
  }

  return <Router />;
}

function App() {
  useEffect(() => {
    if (import.meta.env.DEV) {
      initializeDevTools();
    }
  }, []);

  return (
    <AppErrorBoundary>
      <AppProviders>
        <AppInner />
      </AppProviders>
    </AppErrorBoundary>
  );
}

export default App;
